import type { IOperatorAlertV1, OperatorAlertKind } from './alert.types';

// The delivery policy of an operator alert (docs/verticals/operator-notifications.md
// §7.5) — PURE: what this tab does with one alert, from what the operator is doing.
//
//   | operator state (per tab)     | about the disruption on screen | any other alert in scope      |
//   | operating (Operar, present)  | folds into the view            | the drop: the card, 10 s      |
//   | present, another screen      | —                              | evident dialog, grouped 3 s   |
//   | away (hidden / 2 min idle)   | queued                         | queued → summary on return    |
//
// The drop (PO 2026-10-29): while operating, news about ANOTHER disruption is the evident
// dialog's own card, dropped from the top of the Operar stage and gone by itself after
// 10 s — never a line under the header, which read as news of the disruption on screen.
//
// Plus the cross-cutting rules: own actions (`actorUrn` = me) never alert; an alert older
// than 10 min never opens a dialog (inbox only — brief §6 inv. 3), unless it happened while
// the operator was away (that is exactly what the return summary is for); only the most
// recently focused tab of the operator opens a dialog, the others count (`leader`).
// What each outcome does lives in `alerts.store.ts` (`takeAlert`).

/** An alert older than this never opens a dialog (brief §6 inv. 3, §7.5). */
export const DIALOG_MAX_AGE_MS = 10 * 60_000;
/** Alerts this close to the dialog's first one regroup it (worst first) — «y N más». */
export const GROUP_MS = 3_000;

/**
 * - `none`   — not for this operator: their own action, a kind this build cannot say, or
 *              already read (at or before the read cursor).
 * - `inbox`  — kept and counted; no dialog, no announcement (too old to interrupt).
 * - `queue`  — kept and counted for the summary on return (away).
 * - `fold`   — about the disruption on screen: announced, not counted (the view shows it).
 * - `quiet`  — kept, counted and announced (another tab leads).
 * - `drop`   — kept, counted and announced, and the card drops from the top of «Operar»
 *              for 10 s (operating, this tab leads).
 * - `dialog` — the evident dialog (present on another screen, this tab leads).
 */
export type AlertDelivery = 'none' | 'inbox' | 'queue' | 'fold' | 'quiet' | 'drop' | 'dialog';

/** «Operar» with a disruption on screen: the disruption and its flights in the cache. */
export interface IAlertFocus {
  disruptionUrn: string;
  flightUrns: readonly string[];
}

export interface IDeliveryContext {
  now: number;
  /** The signed-in operator's URN (`/v1/me`); unknown → nobody is excluded. */
  me?: string;
  away: boolean;
  /** When the away spell began — alerts since then belong to the return summary. */
  awaySince?: number;
  /** Set while operating (Operar route, a disruption on screen). */
  focus: IAlertFocus | null;
  /** This tab is the operator's most recently focused one (BroadcastChannel). */
  leader: boolean;
  /** The read cursor (ISO): alerts at or before it were already acknowledged. */
  read?: string | null;
}

const KINDS: ReadonlySet<string> = new Set<OperatorAlertKind>([
  'FLIGHT_DELAYED',
  'FLIGHT_DELAY_CHANGED',
  'FLIGHT_CANCELLED',
  'FLIGHT_DIVERTED',
  'DISRUPTION_OPENED',
]);

/** Severity order (the OS notification's burst lead, lib/os-notify). */
export const SEVERITY_RANK: Readonly<Record<IOperatorAlertV1['severity'], number>> = {
  critical: 2,
  warning: 1,
  info: 0,
};

/**
 * Who leads a burst in the dialog (live review 2026-10-28): what happened to a flight —
 * cancelled, diverted — before the disruption it opened (the concrete news first, never
 * hidden behind «y 1 más»), the disruption before a delay change, a change before a first
 * delay. A kind this build cannot say ranks last.
 */
export const LEAD_RANK: Readonly<Record<OperatorAlertKind, number>> = {
  FLIGHT_CANCELLED: 3,
  FLIGHT_DIVERTED: 3,
  DISRUPTION_OPENED: 2,
  FLIGHT_DELAY_CHANGED: 1,
  FLIGHT_DELAYED: 0,
};

/** `sort` comparator for a burst: the lead rank, then the newest. */
export function byLead(x: IOperatorAlertV1, y: IOperatorAlertV1): number {
  // `kind` is a string at the door (a newer BFF's kind arrives intact): unknown → last.
  const ranks: Readonly<Record<string, number | undefined>> = LEAD_RANK;
  const rank = (a: IOperatorAlertV1): number => ranks[a.kind] ?? -1;
  return rank(y) - rank(x) || Date.parse(y.at) - Date.parse(x.at);
}

/** Is this alert about the disruption on screen (its URN, or one of its cached flights)? */
export function relatesTo(alert: IOperatorAlertV1, focus: IAlertFocus): boolean {
  return (
    alert.disruption?.urn === focus.disruptionUrn ||
    (!!alert.flight && focus.flightUrns.includes(alert.flight.urn))
  );
}

export function deliveryOf(alert: IOperatorAlertV1, ctx: IDeliveryContext): AlertDelivery {
  const at = Date.parse(alert.at);
  if (!KINDS.has(alert.kind)) return 'none';
  if (ctx.me && alert.actorUrn === ctx.me) return 'none';
  if (ctx.read && at <= Date.parse(ctx.read)) return 'none';
  const whileAway = ctx.away && ctx.awaySince !== undefined && at >= ctx.awaySince;
  if (ctx.now - at > DIALOG_MAX_AGE_MS && !whileAway) return 'inbox';
  if (ctx.away) return 'queue';
  if (ctx.focus && relatesTo(alert, ctx.focus)) return 'fold';
  if (!ctx.leader) return 'quiet';
  return ctx.focus ? 'drop' : 'dialog';
}
